import { ActivityId } from '@shared/models/activity-class.model';
import {
  Activity,
  ActivityEnum,
  FutureActivities,
} from '@shared/models/activities.model';
import { ActivityType } from '@shared/models/design/activities/activity-type.model';
import { isOneOf } from '../array.helpers';
import { guards } from '../typeguards.helpers';
import { ActivityInput, EnergyActivity } from './energy.models';

const RESIDENTIAL_ACTIVITIES = [
  ActivityId.Residential,
  ActivityId.PrivateHousing,
  ActivityId.CollectiveHousing,
  ActivityId.Hotel,
  ActivityId.SpecialNeedHousing,
  ActivityId.Hospital,
  ActivityId.DetentionCenter,
  ActivityType.Residential,
] as const;

const OFFICE_ACTIVITIES: ActivityInput[] = [
  ActivityId.Office,
  FutureActivities.Office,
  ActivityId.IndustryOffice,
  ActivityId.Warehouse,
  ActivityId.Brewery,
  ActivityType.Industry,
  ActivityType.Office,
];

const SCHOOL_ACTIVITIES: ActivityInput[] = [
  ActivityId.School,
  FutureActivities.School,
  ActivityId.Public,
  ActivityType.School,
];

const OTHER_ACTIVITIES: ActivityInput[] = [
  ActivityId.CommonAreas,
  ActivityEnum.Other,
  ActivityEnum.LaundryRoom,
  ActivityEnum.Storage,
  ActivityEnum.BuildingUtilities,
  ActivityEnum.Stairwells,
  ActivityType.Other,
  ActivityType.Installations,
  ActivityType.Facades,
  ActivityType.Utilities,
  ActivityType.Storage,
  ActivityType.Stairwells,
  ActivityType.Innerwalls,
  ActivityType.Unset,
];

const PARKING_ACTIVITIES = [
  ActivityEnum.Garage,
  ActivityId.EmployeeParking,
  ActivityId.PublicParking,
  ActivityId.ResidentParking,
  ActivityType.Garage,
] as const;

/**
 * Limit the amount of activity-ids we need use for energy calculations.
 * In activities school is type: 'other' + activityId: 'school'
 * @param activityOrId Can be an activity object or an activity id
 * @returns Normalized energy activity type
 */
export const toEnergyActivity = (
  activityOrId: ActivityInput | Activity | undefined,
): EnergyActivity => {
  if (guards.is.object(activityOrId)) {
    const activityId =
      'activityId' in activityOrId ? activityOrId.activityId : undefined;
    return toEnergyActivity(activityId ?? activityOrId.type);
  }
  if (isOneOf(OFFICE_ACTIVITIES, activityOrId)) {
    return ActivityType.Office;
  }
  if (isOneOf(SCHOOL_ACTIVITIES, activityOrId)) {
    return ActivityType.School;
  }
  if (isOneOf(PARKING_ACTIVITIES, activityOrId)) {
    return ActivityType.Garage;
  }
  if (isOneOf(OTHER_ACTIVITIES, activityOrId)) {
    return ActivityType.Other;
  }
  if (isOneOf(RESIDENTIAL_ACTIVITIES, activityOrId)) {
    return ActivityType.Residential;
  }

  return ActivityType.Residential;
};
